// MARK://dhk8/pos/lib/clients/inventory-catalog
// Lecturas de inventario/proveedores/almacenes para autollenar la captura de
// petty-cash (elegir de lo ya inventariado en vez de teclear texto libre). TODO
// por el grafo (mismo gqlClient del POS): stockItems/suppliers (erp-inventory /
// erp-suppliers) y warehouses (erp-inventory), tenant del JWT vía RLS.
//
// El codegen del POS NO conoce estas queries (su schema es el subconjunto que ya
// usaba: menú, cortes, reportes, contactos…). Por eso los documentos van con
// `parse` + tipos a mano (mismo enfoque que se usa cuando el codegen no cubre una
// query). Lectura pura y NO crítica del money-path: ante cualquier error se
// devuelve [] y la UI degrada a texto libre.
import type { TypedDocumentNode } from '@graphql-typed-document-node/core'
import { parse } from 'graphql'

import { gqlClient } from '../gql.js'

// ── DTOs expuestos a la UI ───────────────────────────────────────────────────

export interface CatalogStockItem {
  stockItemId: string
  productId: string | null
  name: string
  unit: string | null
  unitCostCents: number | null
}

export interface CatalogSupplier {
  id: string
  name: string
  rfc: string | null
}

export interface CatalogWarehouse {
  id: string
  name: string
  code: string | null
}

// ── Documentos (parse + tipos a mano; ver nota de cabecera) ──────────────────

interface StockItemsResult {
  stockItems: {
    items: Array<{
      id: string
      productId: string | null
      ingredientName: string | null
      sku: string | null
      uom: string | null
      unitCostCents: number | null
    }>
  } | null
}

const StockItemsDoc = parse(`
  query PosCatalogStockItems($limit: Int) {
    stockItems(limit: $limit) {
      items {
        id
        productId
        ingredientName
        sku
        uom
        unitCostCents
      }
    }
  }
`) as unknown as TypedDocumentNode<StockItemsResult, { limit?: number }>

interface SuppliersResult {
  suppliers: {
    items: Array<{ id: string; legalName: string | null; rfc: string | null }>
  } | null
}

const SuppliersDoc = parse(`
  query PosCatalogSuppliers($limit: Int) {
    suppliers(limit: $limit) {
      items {
        id
        legalName
        rfc
      }
    }
  }
`) as unknown as TypedDocumentNode<SuppliersResult, { limit?: number }>

interface WarehousesResult {
  warehouses: Array<{ id: string; name: string | null; code: string | null; active: boolean | null }> | null
}

const WarehousesDoc = parse(`
  query PosCatalogWarehouses {
    warehouses {
      id
      name
      code
      active
    }
  }
`) as unknown as TypedDocumentNode<WarehousesResult, Record<string, never>>

function gqlCtx(signal?: AbortSignal): { fetchOptions: { signal: AbortSignal } } | undefined {
  return signal ? { fetchOptions: { signal } } : undefined
}

// ── API ──────────────────────────────────────────────────────────────────────

/** Inventario disponible para elegir en una línea de compra. Nombre a mostrar =
 *  ingredientName ?? sku ?? productId (?? id como último recurso). Degrada a []. */
export async function listStockItems(signal?: AbortSignal): Promise<CatalogStockItem[]> {
  try {
    const res = await gqlClient.query(StockItemsDoc, { limit: 1000 }, gqlCtx(signal)).toPromise()
    if (res.error) return []
    const items = res.data?.stockItems?.items ?? []
    return items.map((it) => ({
      stockItemId: it.id,
      productId: it.productId ?? null,
      name: it.ingredientName ?? it.sku ?? it.productId ?? it.id,
      unit: it.uom ?? null,
      unitCostCents: it.unitCostCents ?? null,
    }))
  } catch {
    return []
  }
}

/** Proveedores para elegir a nivel compra. name = legalName. Degrada a []. */
export async function listSuppliers(signal?: AbortSignal): Promise<CatalogSupplier[]> {
  try {
    const res = await gqlClient.query(SuppliersDoc, { limit: 1000 }, gqlCtx(signal)).toPromise()
    if (res.error) return []
    const items = res.data?.suppliers?.items ?? []
    return items.map((s) => ({
      id: s.id,
      name: s.legalName ?? s.id,
      rfc: s.rfc ?? null,
    }))
  } catch {
    return []
  }
}

/** Almacenes activos para dirigir las líneas capitalizables (inventario). Degrada a []. */
export async function listWarehouses(signal?: AbortSignal): Promise<CatalogWarehouse[]> {
  try {
    const res = await gqlClient.query(WarehousesDoc, {}, gqlCtx(signal)).toPromise()
    if (res.error) return []
    const items = res.data?.warehouses ?? []
    return items
      .filter((w) => w.active !== false)
      .map((w) => ({ id: w.id, name: w.name ?? w.id, code: w.code ?? null }))
  } catch {
    return []
  }
}
